<template lang="pug">
    BlockUI.overflow-hidden
        .h-dvh.flex.flex-col
            .flex
                MainMenu(
                    :haveFile="haveFile"
                    @about="onAboutMenu"
                    @open-file="onOpenFile"
                    @close-file="onCloseFile"
                )
                div.flex-grow.text-center.font-bold {{ windowTitle }}
                ToggleButton.viewer-dark-selector(
                    v-model="darkMode"
                    offIcon="pi pi-sun"
                    onIcon="pi pi-moon"
                    size="small"
                    @change="onDarkMode"
                )
            SvgViewer.grow(
                :annotations="annotations"
                :svgData="svgData"
                :theme="theme"
                @error="onError"
                @event="onEvent"
            )
            AboutDialog(
                v-model:visible="aboutVisible"
                @close="aboutVisible = false"
            )
</template>

<style>
    .viewer-dark-selector .p-togglebutton-label {
        display: none;
    }
</style>

<script setup lang="ts">
import * as vue from 'vue'

import 'primeicons/primeicons.css'

import primeVueAuraTheme from '@primeuix/themes/aura'
import primeVueConfig from 'primevue/config'

//==============================================================================

import type { Annotations, Theme, ViewerEvent } from '../../../src/index'
import SvgViewer from '../../../src/index'

import '../assets/app.css'
import * as vueCommon from '../common/vueCommon'

import AboutDialog from './AboutDialog.vue'

//==============================================================================

const props = defineProps<{
    theme?: Theme
}>()

//==============================================================================

// Setup PrimeVue's theme

const crtInstance = vue.getCurrentInstance();

if (crtInstance) {
    const app = crtInstance.appContext.app;

    if (!app.config.globalProperties.$primevue) {
        app.use(primeVueConfig as unknown as vue.Plugin, {
            theme: {
                preset: primeVueAuraTheme,
                options: {
                    darkModeSelector: '.celldl-dark-mode'
                }
            }
        })
    }
}

vueCommon.useTheme().setTheme(props.theme)

//==============================================================================

const editorTheme = vue.ref<Theme|undefined>(props.theme)

const darkMode = vue.ref<boolean>(props.theme === 'dark')

function onDarkMode() {
    if (darkMode.value) {
        editorTheme.value = 'dark'
    } else {
        editorTheme.value = 'light'
    }
    vueCommon.useTheme().setTheme(editorTheme.value)
}

//==============================================================================
//==============================================================================

const annotations = vue.ref<Annotations>({})

const svgData = vue.ref<string>('')

const windowTitle = vue.ref<string>('')

let currentFileHandle: FileSystemFileHandle|undefined
const haveFile = vue.ref<boolean>(false)

//==============================================================================

async function onOpenFile() {
    const options = {
        excludeAcceptAllOption: true,
        types: [
            {
                description: 'CellDL files',
                accept: {
                    'image/svg+xml': ['.celldl', '.svg'],
                }
            }
        ]
    }
    const fileHandles = await window.showOpenFilePicker(options)
    if (fileHandles.length) {
        currentFileHandle = fileHandles[0]
        if (currentFileHandle) {
            const file = await currentFileHandle.getFile()
            const contents = await file.text()
            svgData.value = contents
            haveFile.value = true
            windowTitle.value = currentFileHandle.name
        }
    }
}

//==============================================================================

function onCloseFile() {
    currentFileHandle = undefined
    svgData.value = ''
    haveFile.value = false
    windowTitle.value = ''
}

//==============================================================================
//==============================================================================

// About dialog.

const aboutVisible = vue.ref<boolean>(false)

function onAboutMenu(): void {
  aboutVisible.value = true
}

//==============================================================================

function onError(msg: string) {
    window.alert(msg)
}

function onEvent(detail: ViewerEvent) {
    console.log(detail)
}

//==============================================================================
//==============================================================================
</script>
